import React, { type ComponentProps, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { ParamListBase } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import {
  TransparentModal,
  useNoticeBannersStackContext,
  useSafeNavigation,
  useSafeRouteParams,
  useSafeRouter,
} from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { useTrackEventOnFocus } from '@order/Telemetry';

import { LocationSearch, NotFoundView } from '../../components';
import { useSetProductDetailsLocation } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsLocationScreen = () => {
  const isFeatureEnabled = useFeatureFlag('CELS-4497-order-product-details-page-enabled'); // prettier-ignore
  const navigation = useSafeNavigation<NativeStackNavigationProp<ParamListBase>>(); // prettier-ignore
  const router = useSafeRouter();
  const isExpoRouterEnabled = useIsExpoRouterEnabled();

  // ─── Route ───────────────────────────────────────────────────────────

  const { baseProductSlug } = useSafeRouteParams<{ baseProductSlug: string }>();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const dismissModal = useCallback(() => {
    if (isExpoRouterEnabled) {
      router?.back();
      return;
    }

    if (navigation?.canGoBack()) {
      navigation.goBack();
      return;
    }

    navigation?.replace('ProductDetails', { baseProductSlug });
  }, [baseProductSlug, isExpoRouterEnabled, navigation, router]);

  // ─────────────────────────────────────────────────────────────────────

  if (isFeatureEnabled) {
    return <ProductDetailsLocationScreenEnabled dismissModal={dismissModal} />;
  }

  return <ProductDetailsLocationScreenDisabled dismissModal={dismissModal} />;
};

// ─────────────────────────────────────────────────────────────────────────────

const ProductDetailsLocationScreenEnabled = (props: Props) => {
  const { dismissModal } = props;

  const { setLastInteractedMenuStore } = useSetProductDetailsLocation();
  const noticeBanners = useNoticeBannersStackContext();
  const { formatMessage } = useIntl();

  // ─── Route ───────────────────────────────────────────────────────────

  const { baseProductSlug } = useSafeRouteParams<{ baseProductSlug: string }>();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const notifyAboutUnavailableLocation = useCallback(() => {
    noticeBanners.push(
      {
        id: UNAVAILABLE_LOCATION_ERROR_BANNER_ID,
        palette: 'caution',
        text: formatMessage(messages.unavailableLocationMessage),
      },
      true,
    );
  }, [formatMessage, noticeBanners]);

  const onLocationSelect = useCallback<NavigateToLocation>(
    (context) => {
      const { activeLocation } = context;

      noticeBanners.clear();

      if (!activeLocation) {
        notifyAboutUnavailableLocation();
        return;
      }

      setLastInteractedMenuStore({
        restaurantId: activeLocation.restaurantId,
        restaurantSlug: activeLocation.restaurantSlug,
        restaurantName: activeLocation.restaurantName,
      });

      dismissModal();
    },
    [
      noticeBanners,
      setLastInteractedMenuStore,
      dismissModal,
      notifyAboutUnavailableLocation,
    ],
  );

  const onDeliveryLocationSelect = useCallback<NavigateToDeliveryLocation>(
    (context) => {
      const { deliveryLocation } = context;

      noticeBanners.clear();

      if (!deliveryLocation) {
        notifyAboutUnavailableLocation();
        return;
      }

      setLastInteractedMenuStore({
        restaurantId: deliveryLocation.restaurantId,
        restaurantSlug: deliveryLocation.restaurantSlug,
        restaurantName: deliveryLocation.restaurantName,
        addressId: deliveryLocation.addressId,
        vendor: deliveryLocation.deliveryVendorName,
        vendorRestaurantId: deliveryLocation.deliveryVendorRestaurantId,
        deliveryFee: deliveryLocation.restaurantDeliveryFee,
      });

      dismissModal();
    },
    [
      dismissModal,
      noticeBanners,
      notifyAboutUnavailableLocation,
      setLastInteractedMenuStore,
    ],
  );

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventOnFocus({
    name: 'product-details.locations.viewed',
    payload: { baseProductSlug },
  });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal
      size="large"
      dialogMaxWidth={DIALOG_MAX_WIDTH}
      dialogMaxHeight={DIALOG_MAX_HEIGHT}
      onClose={dismissModal}
    >
      <LocationSearch
        navigateToLocation={onLocationSelect}
        navigateToDeliveryLocation={onDeliveryLocationSelect}
        interactedLocationRestaurantSlug={undefined}
        interactedLocationDeliveryAddressId={undefined}
        isInsideModal={true}
        onNavClose={dismissModal}
        withNavHeader
      />
    </TransparentModal>
  );
};

const ProductDetailsLocationScreenDisabled = (props: Props) => {
  return (
    <TransparentModal
      dialogMaxWidth={DIALOG_MAX_WIDTH}
      dialogMaxHeight={DIALOG_MAX_HEIGHT}
      onClose={props.dismissModal}
    >
      <NotFoundView withoutFooter />
    </TransparentModal>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const UNAVAILABLE_LOCATION_ERROR_BANNER_ID = 'product-details.location-search.unavailable-location'; // prettier-ignore

const DIALOG_MAX_WIDTH = 1000;
const DIALOG_MAX_HEIGHT = 640;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  unavailableLocationMessage: {
    defaultMessage: 'Something went wrong. Please select a different location.',
    description: 'Product details | Location search | Unavailable location message', // prettier-ignore
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  dismissModal: () => void;
};

type NavigateToLocation = NonNullable<
  ComponentProps<typeof LocationSearch>['navigateToLocation']
>;
type NavigateToDeliveryLocation = NonNullable<
  ComponentProps<typeof LocationSearch>['navigateToDeliveryLocation']
>;
